import Head from 'next/head';
import Header from '../../../components/Header';
import Footer from '../../../components/Footer';
import Hero from '../../../components/Hero';
import geoCoverage from '../../../data/geoCoverage';
import { generateWebPageSchema } from '../../../utils/generateWebPageSchema';

const SERVICES = [
  'complete-home-remodeling',
  'kitchen-remodeling',
  'bathroom-remodeling',
  'adu',
  'exterior-painting',
  'earthquake-retrofit',
  'foundations',
  'pavers',
  'outdoor-living',
  'gutter',
  'energy-efficient-windows',
  'solar-panels',
  'artificial-turf',
  'hvac',
  'asphalt-shingles',
  'tile-roof',
  'flat-roof',
  'metal-roof',
  'tpo-roof',
];

const format = (str) =>
  str.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function ServiceCities({ service }) {
  const baseUrl = 'https://modernbuildremodel.com';

  // ✅ HARD VALIDATION (client safety)
  const isValidService = SERVICES.includes(service);

  const safeService = isValidService ? service : null;
  const formattedService = safeService
    ? format(safeService)
    : 'Home Remodeling';

  const pageUrl = safeService
    ? `${baseUrl}/sitemap/services/${safeService}`
    : `${baseUrl}/sitemap/services`;

  const cities = Object.keys(geoCoverage.california).sort((a, b) =>
    format(a).localeCompare(format(b))
  );

  /* =========================
     Breadcrumb Schema
  ========================== */
  const breadcrumbSchema = safeService && {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Sitemap", "item": `${baseUrl}/sitemap` },
      { "@type": "ListItem", "position": 3, "name": "Services", "item": `${baseUrl}/sitemap/services` },
      { "@type": "ListItem", "position": 4, "name": formattedService, "item": pageUrl }
    ]
  };

  return (
    <>
      <Head>
        {/* ✅ ALWAYS SAFE TITLE */}
        <title>
          {formattedService} Service Areas by City | Modern Build
        </title>
        <meta name="robots" content="index, follow" />
        <meta
          name="title"
          content={`${formattedService} Service Areas by City | Modern Build`}
        />

        <meta
          name="description"
          content={`Explore all ${formattedService} service areas by city across California. Find your location and view available ZIP-specific service pages.`}
        />

        <link rel="canonical" href={pageUrl} />

        <meta name="author" content="Modern Build Home Remodeling" />

        {/* Open Graph */}
        <meta property="og:title" content={`${formattedService} Service Areas by City | Modern Build`} />
        <meta property="og:description" content={`Browse all ${formattedService} locations by city and access detailed ZIP-based service pages.`} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://modernbuildremodel.com/hero/best-home-remodling-contractor-in-los-angeles.webp" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${formattedService} Service Areas by City | Modern Build`} />
        <meta name="twitter:description" content={`Explore ${formattedService} services available across cities in California.`} />
        <meta name="twitter:image" content="https://modernbuildremodel.com/hero/best-home-remodling-contractor-in-los-angeles.webp" />
        <meta name="twitter:site" content="@modernbuildhome" />

        {/* ✅ ONLY ADD SCHEMA IF VALID */}
        {safeService && (
          <>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(
                  generateWebPageSchema({
                    title: `${formattedService} Service Areas by City`,
                    description: `Browse all cities where Modern Build offers ${formattedService}, with direct access to ZIP-specific pages.`,
                    slug: `/sitemap/services/${safeService}`
                  })
                )
              }}
            />

            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(breadcrumbSchema)
              }}
            />
          </>
        )}
      </Head>

      <Header />
      <Hero category="sitemap" page="cities" hasContentH1={true} />

      <main className="container mx-auto px-4 py-10 max-w-6xl">

        <h1 className="text-3xl font-gilda mb-4">
          {formattedService} Service Areas by City
        </h1>

        <p className="mb-6 text-gray-600">
          Select your city to view all available ZIP code pages for {formattedService}.
        </p>

        <ul className="grid md:grid-cols-3 gap-3">
          {cities.map((city) => (
            <li key={city}>
              <a
                href={`/sitemap/services/${safeService}/${city}`}
                className="text-primary hover:underline"
              >
                {format(city)}
              </a>
            </li>
          ))}
        </ul>

      </main>

      <Footer />
    </>
  );
}

/* =========================
   STATIC GENERATION
========================= */

export async function getStaticPaths() {
  const paths = SERVICES.map((service) => ({
    params: { service },
  }));

  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps({ params }) {
  const { service } = params;

  // ✅ SERVER-SIDE VALIDATION (MOST IMPORTANT)
  if (!SERVICES.includes(service)) {
    return {
      notFound: true, // forces 404
    };
  }

  return {
    props: {
      service,
    },
  };
}